import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Calendar, Check, CheckSquare, Cloud,
  CloudRain, CloudSnow, Download, FileText, Image as ImageIcon, LogOut, Mail,
  MapPin, MessageSquare, Share2, Shield, Square, Star, Sun, Trophy, Users, Loader2, X,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { parentApi, clearStoredParentSession, getStoredParentToken, getStoredParentEmail } from "@/lib/parentApi";
import { getStoredAdminPassword, clearStoredAdminPassword } from "@/lib/adminApi";
import { useLanguage } from "@/context/LanguageContext";
import ContactCoachesForm from "@/components/parent-portal/ContactCoachesForm";
import SurveyForm from "@/components/parent-portal/SurveyForm";
import StarReviewForm from "@/components/parent-portal/StarReviewForm";
import ProgressReportModal from "@/components/parent-portal/ProgressReportModal";
import PlayerDetailsModal from "@/components/parent-portal/PlayerDetailsModal";

const LOGO_URL = "/images/ym6gev0b_AE8ECFCA-9BBA-44D9-A534-DED3210EC5A4.png";

// Open-Meteo weathercode → short label + icon
function weatherIcon(code) {
  if (code === 0) return { label: "Clear", Icon: Sun };
  if (code >= 1 && code <= 3) return { label: "Partly cloudy", Icon: Cloud };
  if (code >= 45 && code <= 48) return { label: "Foggy", Icon: Cloud };
  if (code >= 51 && code <= 67) return { label: "Rain", Icon: CloudRain };
  if (code >= 71 && code <= 77) return { label: "Snow", Icon: CloudSnow };
  if (code >= 80 && code <= 82) return { label: "Showers", Icon: CloudRain };
  if (code >= 85 && code <= 86) return { label: "Snow showers", Icon: CloudSnow };
  if (code >= 95 && code <= 99) return { label: "Thunderstorm", Icon: CloudRain };
  return { label: "—", Icon: Cloud };
}

// "Is the weather right now bad enough to warn parents about?"
// Uses Open-Meteo's current_weather.weathercode. Anything from drizzle (51)
// up — including freezing rain, snow, showers, thunderstorms — counts.
function isInclement(weather) {
  const code = weather?.current_weather?.weathercode;
  return typeof code === "number" && code >= 51;
}

function daysUntil(isoDate) {
  if (!isoDate) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(isoDate + "T00:00:00");
  const diffMs = target - today;
  return Math.round(diffMs / 86400000);
}

function formatDateRange(start, end) {
  if (!start) return "";
  const opts = { month: "short", day: "numeric", year: "numeric" };
  const s = new Date(start + "T00:00:00").toLocaleDateString("en-US", opts);
  if (!end || end === start) return s;
  const e = new Date(end + "T00:00:00").toLocaleDateString("en-US", opts);
  return `${s} – ${e}`;
}

export default function ParentDashboard() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [me, setMe] = useState(null);
  const [reports, setReports] = useState({});
  const [availableCamps, setAvailableCamps] = useState([]);
  const [weather, setWeather] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openReport, setOpenReport] = useState(null);          // { report, registration } | null
  const [openKidDetails, setOpenKidDetails] = useState(null);  // kid object or null
  const [photoSelectMode, setPhotoSelectMode] = useState(false);
  const [selectedPhotoIds, setSelectedPhotoIds] = useState(() => new Set());
  const [zipping, setZipping] = useState(false);
  const [photoConsent, setPhotoConsent] = useState(null);   // { consent, mixed, registration_count } | null
  const [savingConsent, setSavingConsent] = useState(false);

  useEffect(() => {
    if (!getStoredParentToken()) {
      navigate("/parent/login", { replace: true });
      return;
    }
    // CRITICAL PATH: /parent/me unblocks the page render. Everything else
    // (weather, photos, progress reports, etc.) fires in parallel and paints
    // progressively as it arrives. Stops the dashboard from waiting on the
    // slowest endpoint (Open-Meteo, ~700ms) before showing anything.
    parentApi
      .get("/parent/me")
      .then((r) => setMe(r.data))
      .catch(() => toast.error(t("parent.loadFail")))
      .finally(() => setLoading(false));

    // Fire-and-forget: each updates its own state when ready.
    parentApi.get("/parent/progress-reports").then((r) => setReports(r.data.by_registration || {})).catch(() => {});
    parentApi.get("/parent/available-camps").then((r) => setAvailableCamps(r.data || [])).catch(() => {});
    parentApi.get("/parent/weather").then((r) => setWeather(r.data)).catch(() => {});
    parentApi.get("/parent/photos").then((r) => setPhotos(r.data || [])).catch(() => {});
    parentApi.get("/parent/photo-consent").then((r) => setPhotoConsent(r.data)).catch(() => {});
    // Best-effort analytics: log a dashboard page view. Not awaited.
    parentApi.post("/parent/track", { page: "dashboard" }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleLogout = () => {
    clearStoredParentSession();
    toast.success(t("parent.signedOut"));
    navigate("/parent/login", { replace: true });
  };

  // Open the OS-native share sheet (iOS share menu, Android share sheet, the
  // browser's web-share UI on supported desktops). On platforms that don't
  // support the Web Share API with files, we fall back to opening the photo
  // in a new tab so the user can long-press / right-click to save or share.
  const sharePhoto = async (photo) => {
    const url = photo.url;
    const filename = (url.split("/").pop() || "photo").split("?")[0];

    // Best path: share the actual image file via navigator.share
    if (navigator.share && navigator.canShare) {
      try {
        const resp = await fetch(url);
        const blob = await resp.blob();
        const file = new File([blob], filename, { type: blob.type || "image/jpeg" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: photo.camp_name || "Burns Elite Baseball",
            text: photo.caption || "",
          });
          return;
        }
      } catch (err) {
        // user cancelled, or fetch/share failed — fall through to fallback
        if (err?.name === "AbortError") return;
      }
    }
    // Fallback: open in a new tab. User can then save/share via the OS chrome.
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const togglePhotoSelectMode = () => {
    setPhotoSelectMode((m) => !m);
    setSelectedPhotoIds(new Set());
  };

  const togglePhotoChecked = (id) => {
    setSelectedPhotoIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const downloadSelectedZip = async () => {
    if (selectedPhotoIds.size === 0) return;
    setZipping(true);
    try {
      const resp = await parentApi.post(
        "/parent/photos/zip",
        { photo_ids: Array.from(selectedPhotoIds) },
        { responseType: "blob" }
      );
      const blobUrl = URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "burns-elite-photos.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      // Exit select mode after a successful download
      setPhotoSelectMode(false);
      setSelectedPhotoIds(new Set());
      toast.success(t("parent.photoZipDone"));
    } catch (err) {
      toast.error(t("parent.photoZipFail"));
    } finally {
      setZipping(false);
    }
  };

  const handleConsentToggle = async (newValue) => {
    if (savingConsent) return;
    setSavingConsent(true);
    // Optimistic update
    setPhotoConsent((prev) => ({ ...(prev || {}), consent: newValue, mixed: false }));
    try {
      const { data } = await parentApi.post("/parent/photo-consent", { consent: newValue });
      setPhotoConsent(data);
      toast.success(newValue ? t("parent.consentSavedYes") : t("parent.consentSavedNo"));
    } catch (err) {
      // Revert on failure
      try {
        const { data } = await parentApi.get("/parent/photo-consent");
        setPhotoConsent(data);
      } catch {}
      toast.error(t("parent.consentSaveFail"));
    } finally {
      setSavingConsent(false);
    }
  };

  const isAdminPreview = !!getStoredAdminPassword();
  const goBackToAdmin = () => {
    // Drop the parent session so logging out doesn't drag the parent token
    // into the admin's next session.
    clearStoredParentSession();
    navigate("/admin");
  };

  // Group registrations by participant_name so each kid is a single card
  const kidsMap = {};
  if (me) {
    [...me.upcoming, ...me.past].forEach((r) => {
      kidsMap[r.participant_name] = kidsMap[r.participant_name] || {
        name: r.participant_name,
        upcoming: [],
        past: [],
      };
      if (me.upcoming.find((u) => u.id === r.id)) {
        kidsMap[r.participant_name].upcoming.push(r);
      } else {
        kidsMap[r.participant_name].past.push(r);
      }
    });
  }
  const kids = Object.values(kidsMap);
  const noUpcoming = me && me.upcoming.length === 0;

  // Deduped camp list for the survey + review pickers. Pulled from THIS parent's
  // own registrations (upcoming + past), so they can only review camps their kid
  // actually attended/is attending.
  const reviewableCamps = me
    ? Array.from(
        new Map(
          [...me.upcoming, ...me.past]
            .filter((r) => r.camp_id)
            .map((r) => [r.camp_id, { id: r.camp_id, name: r.camp_name || "Camp" }])
        ).values()
      )
    : [];

  return (
    <div className="min-h-screen bg-burns-black">
      {isAdminPreview && (
        <div className="bg-burns-gold/15 border-b border-burns-gold/40 text-burns-gold">
          <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-4">
            <span className="text-xs sm:text-sm font-medium">{t("parent.adminPreviewBanner")}</span>
            <Button onClick={goBackToAdmin} size="sm" className="bg-burns-gold hover:bg-burns-gold/80 text-black h-8" data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4 mr-1" /> {t("parent.backToAdmin")}
            </Button>
          </div>
        </div>
      )}
      {isInclement(weather) && (() => {
        const code = weather.current_weather.weathercode;
        const { label, Icon } = weatherIcon(code);
        const tempC = Math.round(weather.current_weather.temperature);
        return (
          <div className="bg-burns-red/20 border-b border-burns-red/50 text-white" data-testid="inclement-banner">
            <div className="container mx-auto px-4 py-2 flex items-center gap-3">
              <Icon className="w-5 h-5 text-burns-red flex-shrink-0" aria-hidden="true" />
              <p className="text-sm">
                <span className="font-medium">{t("parent.inclementBannerTitle")}</span>{" "}
                <span className="text-zinc-200">— {label}, {tempC}°C {t("parent.inclementInMiramichi")}. {t("parent.inclementBannerBody")}</span>
              </p>
            </div>
          </div>
        );
      })()}
      <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Burns Elite Baseball" className="h-12 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-zinc-400 text-sm hidden sm:inline">{getStoredParentEmail()}</span>
            <Button onClick={handleLogout} variant="ghost" className="text-zinc-400 hover:text-white">
              <LogOut className="w-4 h-4 mr-1" /> {t("parent.logout")}
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <h1 className="font-heading text-3xl text-white mb-1">{t("parent.welcome")}</h1>
        <p className="text-zinc-400 mb-8">{t("parent.tagline")}</p>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-burns-gold" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Upcoming countdown banner */}
            {me && me.upcoming.length > 0 && (() => {
              const next = me.upcoming[0];
              const days = daysUntil(next.camp_start_date);
              return (
                <div className="bg-gradient-to-r from-burns-red/20 to-burns-gold/10 border border-burns-gold/30 rounded-lg p-5 flex items-start gap-4">
                  <Trophy className="w-8 h-8 text-burns-gold flex-shrink-0" />
                  <div>
                    <p className="text-burns-gold text-sm font-medium mb-1">{t("parent.nextUp")} {next.participant_name}</p>
                    <h2 className="text-white text-xl font-heading">{next.camp_name}</h2>
                    <p className="text-zinc-400 text-sm mt-1">
                      {formatDateRange(next.camp_start_date, next.camp_end_date)}
                      {next.camp_location && <> · {next.camp_location}</>}
                    </p>
                    {days !== null && days >= 0 && (
                      <p className="text-burns-gold text-sm mt-2 font-medium">
                        {days === 0
                          ? t("parent.starts_today")
                          : days === 1
                          ? t("parent.starts_tomorrow")
                          : t("parent.startsInDays").replace("{days}", days)}
                      </p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Kids + camps */}
            <section>
              <h2 className="text-white font-heading text-xl mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-burns-gold" />
                {t("parent.yourPlayers")} ({kids.length})
              </h2>
              {kids.length === 0 ? (
                <p className="text-zinc-400 text-sm bg-zinc-900 border border-white/10 rounded-lg p-5">
                  {t("parent.noRegistrations").replace("{email}", getStoredParentEmail())}
                </p>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {kids.map((kid) => {
                    const allRegs = [...kid.upcoming, ...kid.past];
                    return (
                      <div key={kid.name} className="bg-zinc-900 border border-white/10 rounded-lg p-5">
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <h3 className="text-white font-heading text-lg">{kid.name}</h3>
                          <button
                            onClick={() => setOpenKidDetails(kid)}
                            className="text-burns-gold hover:text-white text-xs uppercase tracking-wide"
                            data-testid={`open-kid-details-${kid.name.replace(/\s+/g, "-")}`}
                          >
                            {t("parent.editDetails")}
                          </button>
                        </div>

                        {kid.upcoming.length > 0 && (
                          <div className="mb-3">
                            <p className="text-burns-gold text-xs uppercase tracking-wide mb-2">{t("parent.upcoming")}</p>
                            {kid.upcoming.map((r) => (
                              <div key={r.id} className="text-sm text-zinc-300 mb-2 last:mb-0">
                                <p className="font-medium">{r.camp_name}</p>
                                <p className="text-zinc-500 text-xs">
                                  <Calendar className="w-3 h-3 inline mr-1" />
                                  {formatDateRange(r.camp_start_date, r.camp_end_date)}
                                  {r.camp_location && <> · <MapPin className="w-3 h-3 inline mx-1" /> {r.camp_location}</>}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}

                        {kid.past.length > 0 && (
                          <div>
                            <p className="text-zinc-500 text-xs uppercase tracking-wide mb-2">{t("parent.pastCamps")}</p>
                            {kid.past.map((r) => (
                              <div key={r.id} className="text-sm text-zinc-400 mb-2 last:mb-0">
                                <p>{r.camp_name}</p>
                                <p className="text-zinc-600 text-xs">{formatDateRange(r.camp_start_date, r.camp_end_date)}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Progress reports for any of this kid's registrations */}
                        {allRegs.some((r) => reports[r.id]?.reports?.length > 0) && (
                          <div className="border-t border-white/5 pt-3 mt-3">
                            <p className="text-burns-gold text-xs uppercase tracking-wide mb-2 flex items-center gap-1">
                              <FileText className="w-3 h-3" /> {t("parent.progressReports")}
                            </p>
                            {allRegs.map((r) => {
                              const group = reports[r.id];
                              if (!group?.reports?.length) return null;
                              return (
                                <div key={r.id} className="mb-3 last:mb-0">
                                  <p className="text-zinc-500 text-xs italic mb-1">{r.camp_name}</p>
                                  <div className="space-y-1">
                                    {group.reports.map((report) => (
                                      <button
                                        key={report.id}
                                        onClick={() => setOpenReport({ report, registration: r })}
                                        className="w-full bg-zinc-950 border border-white/5 hover:border-burns-gold rounded p-2 text-left transition-colors"
                                        data-testid={`open-report-${report.id}`}
                                      >
                                        <div className="flex items-center justify-between">
                                          <span className="text-zinc-300 text-xs">
                                            {new Date(report.sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                                            {report.language === "fr" && <span className="ml-2 text-burns-gold uppercase text-[10px]">FR</span>}
                                          </span>
                                          <span className="text-burns-gold text-[10px] uppercase tracking-wide">{t("parent.viewReport")}</span>
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Camp Photos — compact horizontal scroller. Tap to share, or
                use Select mode to download multiple as a ZIP. */}
            {photos.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-3 gap-3">
                  <h2 className="text-white font-heading text-xl flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-burns-gold" />
                    {t("parent.campPhotos")}
                    <span className="text-zinc-500 text-sm font-normal">({photos.length})</span>
                  </h2>
                  <div className="flex items-center gap-2">
                    {photoSelectMode && selectedPhotoIds.size > 0 && (
                      <Button
                        size="sm"
                        onClick={downloadSelectedZip}
                        disabled={zipping}
                        className="bg-burns-red hover:bg-burns-red/80 text-white h-8"
                        data-testid="download-selected-zip"
                      >
                        {zipping ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                        {t("parent.photoDownloadSelected").replace("{n}", selectedPhotoIds.size)}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={togglePhotoSelectMode}
                      className="text-zinc-300 hover:text-white h-8"
                      data-testid="toggle-photo-select"
                    >
                      {photoSelectMode ? <X className="w-4 h-4 mr-1" /> : <CheckSquare className="w-4 h-4 mr-1" />}
                      {photoSelectMode ? t("parent.photoCancelSelect") : t("parent.photoSelectMultiple")}
                    </Button>
                  </div>
                </div>
                <p className="text-zinc-500 text-xs mb-3">
                  {photoSelectMode ? t("parent.photoSelectHint") : t("parent.photosHint")}
                </p>
                <div className="flex gap-3 overflow-x-auto pb-3 -mx-1 px-1" data-testid="parent-photo-gallery">
                  {photos.map((p) => {
                    const checked = selectedPhotoIds.has(p.id);
                    return (
                      <button
                        key={p.id}
                        onClick={() => (photoSelectMode ? togglePhotoChecked(p.id) : sharePhoto(p))}
                        className={
                          "relative flex-shrink-0 w-32 h-32 rounded-md overflow-hidden border transition-colors group " +
                          (checked
                            ? "border-burns-red ring-2 ring-burns-red/60"
                            : "border-white/10 hover:border-burns-gold")
                        }
                        title={(p.caption ? p.caption + " · " : "") + (p.camp_name || "")}
                      >
                        <img src={p.url} alt={p.caption || p.camp_name || "Camp photo"} className="w-full h-full object-cover" loading="lazy" />
                        {photoSelectMode ? (
                          <div className={"absolute top-1 left-1 w-6 h-6 rounded flex items-center justify-center " + (checked ? "bg-burns-red text-white" : "bg-black/60 text-white/60")}>
                            {checked ? <Check className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                          </div>
                        ) : (
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                            <Share2 className="w-6 h-6 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Available camps */}
            {(noUpcoming || availableCamps.length > 0) && (
              <section>
                <h2 className="text-white font-heading text-xl mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-burns-gold" />
                  {t("parent.registerCTA")}
                </h2>
                {availableCamps.length === 0 ? (
                  <p className="text-zinc-400 text-sm bg-zinc-900 border border-white/10 rounded-lg p-5">
                    {t("parent.noAvailableCamps")}
                  </p>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    {availableCamps.map((c) => (
                      <Link key={c.id} to={`/register?camp=${c.id}`} className="block bg-zinc-900 border border-white/10 rounded-lg p-4 hover:border-burns-gold transition-colors">
                        <p className="text-white font-medium mb-1">{c.name}</p>
                        <p className="text-zinc-500 text-xs">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {formatDateRange(c.start_date, c.end_date)} · {c.age_group}
                        </p>
                        {c.location && (
                          <p className="text-zinc-500 text-xs mt-1">
                            <MapPin className="w-3 h-3 inline mr-1" />{c.location}
                          </p>
                        )}
                        <p className="text-burns-gold text-xs mt-2">{t("parent.registerArrow")}</p>
                      </Link>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Contact the coaches */}
            <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
              <h2 className="text-white font-heading text-xl mb-3 flex items-center gap-2">
                <Mail className="w-5 h-5 text-burns-gold" />
                {t("parent.contactTitle")}
              </h2>
              <p className="text-zinc-500 text-sm mb-4">{t("parent.contactSubtitle")}</p>
              <ContactCoachesForm />
            </section>

            {/* Survey */}
            <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
              <h2 className="text-white font-heading text-xl mb-3 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-burns-gold" />
                {t("parent.surveyTitle")}
              </h2>
              <p className="text-zinc-500 text-sm mb-4">{t("parent.surveySubtitle")}</p>
              <SurveyForm camps={reviewableCamps} />
            </section>

            {/* Star review */}
            <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
              <h2 className="text-white font-heading text-xl mb-3 flex items-center gap-2">
                <Star className="w-5 h-5 text-burns-gold" />
                {t("parent.reviewTitle")}
              </h2>
              <p className="text-zinc-500 text-sm mb-4">{t("parent.reviewSubtitle")}</p>
              <StarReviewForm
                camps={reviewableCamps}
                defaultName={(me && (me.upcoming[0] || me.past[0]) && `Parent of ${(me.upcoming[0] || me.past[0]).participant_name}`) || ""}
              />
            </section>

            {/* Privacy & Photo Sharing */}
            {photoConsent && photoConsent.registration_count > 0 && (
              <section className="bg-zinc-900 border border-white/10 rounded-lg p-5">
                <h2 className="text-white font-heading text-lg mb-1 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-burns-gold" />
                  {t("parent.privacyTitle")}
                </h2>
                <p className="text-zinc-500 text-xs mb-4">{t("parent.privacySubtitle")}</p>
                <div className="flex items-start justify-between gap-4 bg-zinc-950 border border-white/10 rounded-md p-4">
                  <div className="flex-1">
                    <p className="text-white font-medium text-sm">{t("parent.consentLabel")}</p>
                    <p className="text-zinc-400 text-xs mt-1 leading-relaxed">{t("parent.consentBody")}</p>
                    {photoConsent.mixed && (
                      <p className="text-burns-gold text-xs mt-2">{t("parent.consentMixed")}</p>
                    )}
                  </div>
                  <Switch
                    checked={!!photoConsent.consent}
                    onCheckedChange={handleConsentToggle}
                    disabled={savingConsent}
                    className="data-[state=checked]:bg-burns-red mt-1"
                    data-testid="photo-consent-toggle"
                  />
                </div>
                <p className="text-zinc-600 text-xs mt-2">
                  {t("parent.consentScope").replace("{n}", photoConsent.registration_count)}
                </p>
              </section>
            )}

            {/* Compact weather strip — single horizontal row, tight padding */}
            {weather && weather.daily && (
              <section className="bg-zinc-900/60 border border-white/10 rounded-lg px-4 py-3">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    <Cloud className="w-4 h-4 text-burns-gold" />
                    <h3 className="text-zinc-300 text-xs uppercase tracking-wide">{t("parent.weather")}</h3>
                  </div>
                  <span className="text-zinc-600 text-xs">°C</span>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {weather.daily.time.map((day, i) => {
                    const code = weather.daily.weathercode[i];
                    const { label, Icon } = weatherIcon(code);
                    const max = Math.round(weather.daily.temperature_2m_max[i]);
                    const min = Math.round(weather.daily.temperature_2m_min[i]);
                    const isToday = i === 0;
                    return (
                      <div key={day} className="flex flex-col items-center text-center">
                        <p className="text-zinc-500 text-[10px] uppercase">
                          {isToday ? t("parent.today") : new Date(day + "T00:00:00").toLocaleDateString("en-US", { weekday: "short" })}
                        </p>
                        <Icon className="w-4 h-4 text-burns-gold my-1" aria-label={label} />
                        <p className="text-white text-sm leading-none">{max}°<span className="text-zinc-500"> / {min}°</span></p>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        )}
      </main>

      <ProgressReportModal
        open={!!openReport}
        onOpenChange={(o) => { if (!o) setOpenReport(null); }}
        report={openReport?.report}
        registration={openReport?.registration}
      />
      <PlayerDetailsModal
        open={!!openKidDetails}
        onOpenChange={(o) => { if (!o) setOpenKidDetails(null); }}
        kid={openKidDetails}
        onSaved={() => {
          // Refresh /parent/me so the dashboard shows the new info immediately.
          parentApi.get("/parent/me").then((r) => setMe(r.data)).catch(() => {});
          setOpenKidDetails(null);
        }}
      />
    </div>
  );
}
